'use client';

import type { OverheadProjectResponse } from '@/app/models/overhead';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import TruncatedTextWithTooltip from '@/app/utils/truncatedTextWithTooltip';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import { TableCell } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type EventTableParams = {
  rows: OverheadProjectResponse[];
};

export default function OverheadTable(props: EventTableParams) {
  const navigate = useNavigate();
  const { t } = useTranslation(['overhead', 'common']);
  const { rows } = props;

  const columns: Column[] = [
    { key: 'project', label: t('project') },
    { key: 'branch', label: t('common:branch') },
    { key: 'payroll', label: `${t('payroll')}` },
    { key: 'toPay', label: `${t('to_pay')}` },
    { key: 'paidValue', label: `${t('paid.value')}` },
    { key: 'priority', label: t('priority'), align: 'center' },
  ];

  const renderRow = (row: OverheadProjectResponse) => (
    <>
      <TableCell sx={bodyCellSx}>
        <TruncatedTextWithTooltip value={`${row.project.code} - ${row.project.name}`} size={80} sx={bodyCellSx} />
      </TableCell>
      <TableCell sx={bodyCellSx}>{`${row.project.branchName}`}</TableCell>
      <TableCell sx={bodyCellSx}>{`${formatterMonetaryValue(row.totalValue)}`}</TableCell>
      <TableCell sx={bodyCellSx}>{`${formatterMonetaryValue(row.overheadValue)}`}</TableCell>
      <TableCell sx={bodyCellSx}>{`${formatterMonetaryValue(row.balanceValue)}`}</TableCell>
      <TableCell sx={bodyCellSx} align="center">{`${row.priority}`}</TableCell>
    </>
  );

  // Use the row ID as the unique key
  const getRowKey = (row: OverheadProjectResponse) => row.id;

  return (
    <DataTable
      columns={columns}
      data={rows}
      renderRow={renderRow}
      getRowKey={getRowKey}
      emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
      onRowClick={(row) => navigate(`${row.id}`)}
    />
  );
}
